import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../api/axiosInstance';
import ToastContainer from '../Components/ToastContainer';
import './ChangePassword.css';

// Password rules to display
const PASSWORD_RULES = [
  'At least 8 characters long',
  'At least one uppercase letter (A-Z)',
  'At least one lowercase letter (a-z)',
  'At least one number (0-9)',
  'At least one special character (!@#$%^&*)',
  'Cannot contain your email, first name, or last name',
  'Cannot reuse your last 3 passwords'
];

const ChangePassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRequired = searchParams.get('required') === 'true';

  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '' });

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/signin');
    }
  }, [navigate]);

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    return {
      score: Math.min(score, 5),
      label: labels[Math.min(score, 5)]
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'newPassword') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords
    if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (formData.oldPassword === formData.newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    try {
      setIsLoading(true);
      const response = await axiosInstance.post('/Corrohealth/api/password/change', {
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      });

      if (response.data.status === 200 || response.status === 200) {
        toast.success('Password changed successfully!');

        // Clear the requirePasswordChange flag
        localStorage.removeItem('requirePasswordChange');

        // If this was a required password change, redirect to home
        if (isRequired) {
          // Get the redirect path based on user's role/permissions
          const token = localStorage.getItem('authToken');
          if (token) {
            // Import dynamically to avoid circular dependencies
            const { getRedirectPath } = await import('../utils/jwtUtils');
            const redirectPath = getRedirectPath(token);
            if (redirectPath) {
              navigate(redirectPath);
            } else {
              navigate('/');
            }
          } else {
            navigate('/signin');
          }
        } else {
          // Go back to previous page
          navigate(-1);
        }
      } else {
        toast.error(response.data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to change password. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthColor = (score) => {
    const colors = ['#ff4444', '#ff8800', '#ffcc00', '#88cc00', '#00cc44', '#00cc88'];
    return colors[score] || colors[0];
  };

  return (
    <div className="change-password-container">
      <div className="change-password-card">
        <div className="change-password-header">
          <h1>Change Password</h1>
          {isRequired && (
            <div className="required-notice">
              <span className="warning-icon">!</span>
              <p>Your password was set by an administrator. You must change it before continuing.</p>
            </div>
          )}
        </div>

        <div className="password-rules-section">
          <h3>Password Requirements</h3>
          <ul className="password-rules-list">
            {PASSWORD_RULES.map((rule, index) => (
              <li key={index}>{rule}</li>
            ))}
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="change-password-form">
          <div className="form-group">
            <label htmlFor="oldPassword">Current Password</label>
            <div className="password-input-wrapper">
              <input
                type={showOldPassword ? 'text' : 'password'}
                id="oldPassword"
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleInputChange}
                placeholder="Enter your current password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter your new password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {formData.newPassword && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div
                    className="strength-fill"
                    style={{
                      width: `${(passwordStrength.score / 5) * 100}%`,
                      backgroundColor: getStrengthColor(passwordStrength.score)
                    }}
                  />
                </div>
                <span className="strength-label" style={{ color: getStrengthColor(passwordStrength.score) }}>
                  {passwordStrength.label}
                </span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your new password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <span className="error-text">Passwords do not match</span>
            )}
            {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
              <span className="success-text">Passwords match</span>
            )}
          </div>

          <div className="form-actions">
            {!isRequired && (
              <button
                type="button"
                className="btn-cancel"
                onClick={() => navigate(-1)}
                disabled={isLoading}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="btn-submit"
              disabled={isLoading || (formData.newPassword !== formData.confirmPassword)}
            >
              {isLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default ChangePassword;
